import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, ErrorDeApi, type Cliente, type EstadoDeFolio, type FolioApartado, type SerieDeFolios } from '../servicios/api'

/** Los instantes del API vienen en UTC: se muestran en hora local (ver fechas.ts). */
const instante = (iso: string) => new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })

const SIN_PERMISO = 'Tu rol no puede cambiar la numeración de los comprobantes: pide a un contador o a un administrador del despacho que lo haga.'

const mensaje = (e: unknown, respaldo: string) =>
  e instanceof ErrorDeApi ? (e.estado === 403 ? SIN_PERMISO : e.message) : respaldo

const ESTADOS: Record<EstadoDeFolio, string> = {
  Reservado: 'Pendiente',
  Consumido: 'Timbrado',
  Descartado: 'Descartado',
}

/**
 * Series y folios del cliente (RF-1005). Cada comprobante que se timbre desde ContaQuick aparta el
 * siguiente folio de su serie antes de enviarse; un folio apartado nunca se borra. Los que se
 * quedaron reservados sin timbre son los pendientes: se reintentan o se descartan con su motivo,
 * para que el hueco en la numeración tenga explicación.
 */
export function SeriesYFolios() {
  const { id: clienteId } = useParams<{ id: string }>()
  const [cliente, setCliente] = useState<Cliente>()
  const [series, setSeries] = useState<SerieDeFolios[]>()
  const [folios, setFolios] = useState<FolioApartado[]>([])
  const [soloPendientes, setSoloPendientes] = useState(true)
  const [errorCarga, setErrorCarga] = useState('')
  const [errorAccion, setErrorAccion] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const [serie, setSerie] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [folioInicial, setFolioInicial] = useState('')
  const [descartando, setDescartando] = useState<string>()
  const [motivo, setMotivo] = useState('')

  useEffect(() => {
    if (!clienteId) return
    api.obtenerCliente(clienteId).then(setCliente).catch(() => setCliente(undefined))
  }, [clienteId])

  async function cargar() {
    if (!clienteId) return
    setErrorCarga('')
    try {
      const [s, f] = await Promise.all([
        api.seriesDeFolios(clienteId),
        api.foliosApartados(clienteId, soloPendientes ? 'Reservado' : undefined),
      ])
      setSeries(s)
      setFolios(f)
    } catch (e) {
      setSeries(undefined)
      setErrorCarga(e instanceof ErrorDeApi ? e.message : 'No se pudieron cargar las series del cliente.')
    }
  }

  useEffect(() => {
    void cargar()
  }, [clienteId, soloPendientes])

  async function accion(hacer: () => Promise<unknown>, respaldo: string) {
    setOcupado(true)
    setErrorAccion('')
    try {
      await hacer()
      await cargar()
      return true
    } catch (e) {
      setErrorAccion(mensaje(e, respaldo))
      return false
    } finally {
      setOcupado(false)
    }
  }

  async function darDeAlta() {
    if (!clienteId) return
    const inicial = Number(folioInicial)
    const ok = await accion(() => api.crearSerieDeFolios(clienteId, {
      serie: serie.trim(),
      ...(descripcion.trim() && { descripcion: descripcion.trim() }),
      ...(folioInicial && { folioInicial: inicial }),
    }), 'No se pudo dar de alta la serie.')
    if (ok) {
      setSerie('')
      setDescripcion('')
      setFolioInicial('')
    }
  }

  async function descartar(folioId: string) {
    if (!clienteId) return
    const ok = await accion(() => api.descartarFolio(clienteId, folioId, motivo.trim()), 'No se pudo descartar el folio.')
    if (ok) {
      setDescartando(undefined)
      setMotivo('')
    }
  }

  if (!clienteId) return <p>Cargando…</p>

  return (
    <>
      <p className="sutil"><Link to={`/clientes/${clienteId}`}>← {cliente?.razonSocial ?? 'Expediente'}</Link></p>

      <header className="encabezado linea-base">
        <h1>Series y folios</h1>
        {cliente && <span className="encabezado-sub mono">{cliente.rfc} · {cliente.razonSocial}</span>}
      </header>

      <p className="nota">
        Cada comprobante que se timbre desde ContaQuick toma el siguiente folio de su serie antes de enviarse al PAC,
        así dos comprobantes nunca comparten número. Un folio apartado no se borra: se timbra o se descarta con su motivo.
      </p>

      {errorCarga && <p className="error" role="alert">{errorCarga}</p>}
      {errorAccion && <p className="error" role="alert">{errorAccion}</p>}

      <section aria-labelledby="titulo-series">
        <h2 id="titulo-series">Series</h2>
        {series && series.length === 0 && (
          <p className="vacio">Este cliente aún no tiene series. Da de alta la primera abajo: por ejemplo A, FAC o NC.</p>
        )}
        {series && series.length > 0 && (
          <div className="tabla-envoltura">
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col">Serie</th>
                  <th scope="col">Descripción</th>
                  <th scope="col" className="num">Siguiente folio</th>
                  <th scope="col" className="num">Pendientes</th>
                  <th scope="col">Estado</th>
                  <th scope="col">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {series.map((s) => (
                  <tr key={s.id}>
                    <th scope="row" className="mono">{s.serie}</th>
                    <td>{s.descripcion ?? <span className="sutil">—</span>}</td>
                    <td className="num mono">{s.siguienteFolio}</td>
                    <td className="num">{s.foliosPendientes}</td>
                    <td>{s.activa ? 'Activa' : 'Desactivada'}</td>
                    <td>
                      <button
                        className="enlace"
                        disabled={ocupado}
                        onClick={() => void accion(
                          () => (s.activa ? api.desactivarSerieDeFolios : api.reactivarSerieDeFolios)(clienteId, s.id),
                          `No se pudo ${s.activa ? 'desactivar' : 'reactivar'} la serie.`,
                        )}
                      >
                        {s.activa ? 'Desactivar' : 'Reactivar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <form
          className="filtros"
          aria-label="Alta de serie"
          onSubmit={(e) => {
            e.preventDefault()
            void darDeAlta()
          }}
        >
          <label>
            Serie
            <input value={serie} onChange={(e) => setSerie(e.target.value)} maxLength={25} required placeholder="FAC" />
          </label>
          <label>
            Descripción
            <input value={descripcion} onChange={(e) => setDescripcion(e.target.value)} maxLength={200}
              placeholder="Facturas de mostrador" />
          </label>
          <label>
            Folio inicial
            <input type="number" min="1" step="1" value={folioInicial} onChange={(e) => setFolioInicial(e.target.value)}
              placeholder="1" />
          </label>
          <button type="submit" className="primario" disabled={ocupado}>Dar de alta serie</button>
        </form>
        <p className="nota">Si el cliente ya timbraba en otro sistema, el folio inicial es el que sigue al último que usó ahí.</p>
      </section>

      <section aria-labelledby="titulo-folios">
        <header className="encabezado">
          <h2 id="titulo-folios">{soloPendientes ? 'Folios pendientes' : 'Folios apartados'}</h2>
          <select
            className="compacta"
            style={{ width: 'auto' }}
            aria-label="Mostrar"
            value={soloPendientes ? 'pendientes' : 'todos'}
            onChange={(e) => setSoloPendientes(e.target.value === 'pendientes')}
          >
            <option value="pendientes">Solo pendientes</option>
            <option value="todos">Todos</option>
          </select>
        </header>

        {series && folios.length === 0 && (
          <p className="vacio">
            {soloPendientes
              ? 'No hay folios pendientes: todo folio apartado ya se timbró o se descartó.'
              : 'Aún no se ha apartado ningún folio de este cliente.'}
          </p>
        )}
        {folios.length > 0 && (
          <div className="tarjeta"><ul className="lista-compacta">
            {folios.map((f) => (
              <li key={f.id}>
                <span>
                  <strong className="mono">{f.serie}-{f.folio}</strong> · {ESTADOS[f.estado]}
                  {f.proposito && ` · ${f.proposito}`}
                  <span className="sutil"> · apartado el {instante(f.reservadoEnUtc)}</span>
                  {f.estado === 'Consumido' && f.uuidTimbrado && <span className="sutil mono"> · {f.uuidTimbrado}</span>}
                  {f.estado === 'Descartado' && <span className="sutil"> · {f.motivoDescarte}</span>}
                </span>
                {f.estado === 'Reservado' && descartando !== f.id && (
                  <span className="acciones-de-fila">
                    <button className="enlace" onClick={() => {
                      setDescartando(f.id)
                      setMotivo('')
                    }}>Descartar</button>
                  </span>
                )}
                {descartando === f.id && (
                  <form
                    className="acciones-de-fila"
                    aria-label={`Descartar ${f.serie}-${f.folio}`}
                    onSubmit={(e) => {
                      e.preventDefault()
                      void descartar(f.id)
                    }}
                  >
                    <input value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={500} required
                      aria-label="Motivo del descarte" placeholder="Por qué no se timbrará" />
                    <button type="submit" className="primario" disabled={ocupado}>Confirmar descarte</button>
                    <button type="button" className="enlace" onClick={() => setDescartando(undefined)}>Cancelar</button>
                  </form>
                )}
              </li>
            ))}
          </ul></div>
        )}
      </section>
    </>
  )
}
